/**
 * Shared rendering parts for condition renderers.
 * Matches ~/code/ui condition-editor-v2 parts (Key, Value, Times, Within, Filters).
 */

import { Box, Text } from '@customerio/pluma-components/react';
import plur from 'plur';
import * as styles from './condition-parts.css';
import { formatWithin } from '../parse/parse-helpers';
import type { ConditionFilter } from '../types';

export function Bold({ children }: { children: React.ReactNode }) {
  return (
    <Text as="span" weight="semibold" color="bold">
      {children}
    </Text>
  );
}

export function Times({ times }: { times?: number }) {
  if (times == null || times <= 1) return null;
  return (
    <>
      {' '}
      at least <Bold>{times}</Bold> {plur('time', times)}
    </>
  );
}

export function Within({ within }: { within?: number }) {
  if (within == null || within === 0) return null;
  return (
    <>
      {' '}
      within <Bold>{formatWithin(within)}</Bold>
    </>
  );
}

export function FieldOperatorValue({
  field,
  operator,
  value,
  value2,
}: {
  field?: string;
  operator?: string;
  value?: string;
  value2?: string;
}) {
  return (
    <>
      {field != null ? <Bold>{field}</Bold> : null}
      {operator != null ? <> {operator}</> : null}
      {value != null ? (
        <>
          {' '}
          <Bold>{value}</Bold>
        </>
      ) : null}
      {value2 != null ? (
        <>
          {' '}
          and <Bold>{value2}</Bold>
        </>
      ) : null}
    </>
  );
}

export function SubFilters({
  filters,
  filtersOperator,
  label,
}: {
  filters?: ConditionFilter[];
  filtersOperator?: 'and' | 'or';
  /** Optional type label shown after the operator (e.g. "object attributes"). */
  label?: string;
}) {
  if (filters == null || filters.length === 0) return null;
  const operatorLabel = filtersOperator === 'or' ? 'any' : 'all';
  return (
    <>
      {' '}
      and matches {operatorLabel}
      {label != null ? ` ${label}` : ''}:
      <Box display="flex" gap="100" alignItems="flex-start" mt="100" flexWrap="wrap">
        {filters.map((filter, index) => (
          <Text
            key={`${index}-${filter.field}-${filter.operator}`}
            size="xs"
            color="subtle"
            display="inline-block"
            p="050"
            overflowWrap="anywhere"
            className={styles.filterChip}
          >
            <Bold>{filter.field}</Bold> {filter.operator}
            {filter.value != null ? (
              <>
                {' '}
                <Bold>{filter.value}</Bold>
              </>
            ) : null}
            {filter.value2 != null ? (
              <>
                {' '}
                and <Bold>{filter.value2}</Bold>
              </>
            ) : null}
          </Text>
        ))}
      </Box>
    </>
  );
}
